import { mapActions } from 'vuex';

import { api } from '_/services/api';

// In-flight match observability. 5s is deliberate — the panes surface
// SR-importer stalls and push-fanout failures that go critical inside a
// minute (60s amber, 120s red thresholds on the live-match pane). 30s
// would be too stale to catch a stall before on-call notices it.
const POLL_INTERVAL_MS = 5000;


function matchStatusBadge (status) {
    switch (status) {
        case 'live':        return 'label-danger';
        case 'half_time':   return 'label-warning';
        case 'full_time':   return 'label-success';
        case 'scheduled':   return 'label-default';
        default:            return 'label-default';
    }
}


function tickStatusBadge (row) {
    if (row.has_error)         return 'label-danger';
    if (row.completed_at)      return 'label-success';
    return 'label-warning';
}


function tickStatusLabel (row) {
    if (row.has_error)         return 'error';
    if (row.completed_at)      return 'ok';
    return 'running';
}


function formatRelative (iso) {
    if (!iso) return '—';
    const t = new Date(iso).getTime();
    const dt = Date.now() - t;
    if (Number.isNaN(t)) return '—';
    if (dt < 60_000)       return Math.floor(dt / 1000) + 's ago';
    if (dt < 3_600_000)    return Math.floor(dt / 60_000) + 'm ago';
    if (dt < 86_400_000)   return Math.floor(dt / 3_600_000) + 'h ago';
    return Math.floor(dt / 86_400_000) + 'd ago';
}


function formatTime (iso) {
    if (!iso) return '—';
    // Backend sometimes emits "YYYY-MM-DD HH:MM:SS" (DB native) instead of
    // ISO 8601 — normalise to T-separator so Safari parses it.
    const normalised = typeof iso === 'string' ? iso.replace(' ', 'T') : iso;
    const d = new Date(normalised);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString();
}


function formatDuration (ms) {
    if (ms === null || ms === undefined) return '—';
    if (ms < 1000) return ms + 'ms';
    return (ms / 1000).toFixed(1) + 's';
}


function formatSeconds (sec) {
    if (sec === null || sec === undefined) return '—';
    if (sec < 60) return sec + 's ago';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return s ? m + 'm ' + s + 's ago' : m + 'm ago';
}


function formatNumber (n) {
    if (n === null || n === undefined) return '—';
    return new Intl.NumberFormat().format(n);
}


function formatPercent (rate) {
    if (rate === null || rate === undefined) return '—';
    return (rate * 100).toFixed(1) + '%';
}


function shortSrId (id) {
    if (!id) return '—';
    return id.replace(/^sr:[^:]+:/, '');
}


function feedStallClass (sec) {
    // Stall detection keyed on the importer HEARTBEAT (seconds_since_update),
    // not on event recency. The match-summary stage touches every live match
    // ~every 60s, so missing a cycle is the real signal: >60s amber (one
    // missed poll), >120s red (two+ — the feed for this match has stalled).
    // Do NOT point this at seconds_since_last_event — event gaps are normal
    // (quiet match / half-time) and would paint every row red.
    if (sec === null || sec === undefined) return '';
    if (sec > 120) return 'text-stale';
    if (sec > 60)  return 'text-warning-amber';
    return '';
}


// PCMS controllers using `response()->json(...)` are wrapped twice by the
// HTTP layer: `{message, body: {message, body: …actual…}}`. The
// pipeline-monitor proxy is the odd one out (raw FastAPI passthrough).
// LiveMonitorController uses `response()->json` like every other PCMS
// controller, so unwrap both layers, defensively falling back to a single
// unwrap or the raw response.
function unwrapPayload (response) {
    const outer = response && response.body ? response.body : response;
    return outer && outer.body ? outer.body : outer;
}


export default {
    name: 'LiveMonitor',

    data () {
        return {
            liveMatches: [],
            srTicks: [],
            pushDispatches: [],
            pushLastHour: null,
            whatsapp: null,
            snapshotAt: null,
            loading: false,
            error: null,
            _pollHandle: null,
        };
    },

    computed: {
        breadcrumbItems () {
            return [{
                text: 'Live Monitor',
                link: 'live-monitor',
            }];
        },

        hasAnyData () {
            return this.liveMatches.length > 0
                || this.srTicks.length > 0
                || this.pushDispatches.length > 0
                || (this.whatsapp && (this.whatsapp.last_hour.inbound > 0 || this.whatsapp.subscribers > 0));
        },

        pushLastHourLabel () {
            const lh = this.pushLastHour;
            if (!lh || !lh.total) return 'no dispatches last hour';
            return formatNumber(lh.successful) + '/' + formatNumber(lh.total)
                + ' delivered last hour · ' + formatPercent(lh.success_rate);
        },
    },

    methods: {
        ...mapActions('ui', [
            'animateWrapper',
            'setBodyClass',
            'setBreadcrumbItems',
        ]),

        matchStatusBadge,
        tickStatusBadge,
        tickStatusLabel,
        formatRelative,
        formatTime,
        formatDuration,
        formatSeconds,
        formatNumber,
        formatPercent,
        shortSrId,
        feedStallClass,

        async refresh () {
            this.loading = true;
            try {
                // Four parallel fetches — the panes share a 5s poll
                // budget, so any serial chain would multiply the staleness.
                const [matchesRes, ticksRes, pushRes, waRes] = await Promise.all([
                    api.get('admin/live-monitor/live-matches'),
                    api.get('admin/live-monitor/sr-ticks?limit=50'),
                    api.get('admin/live-monitor/push-dispatch?limit=100'),
                    api.get('admin/live-monitor/whatsapp'),
                ]);

                const matches = unwrapPayload(matchesRes);
                const ticks   = unwrapPayload(ticksRes);
                const push    = unwrapPayload(pushRes);
                const wa      = unwrapPayload(waRes);

                this.liveMatches    = (matches && matches.data) || [];
                this.srTicks        = (ticks && ticks.data) || [];
                this.pushDispatches = (push && push.data) || [];
                this.pushLastHour   = (push && push.last_hour) || null;
                this.whatsapp       = (wa && wa.data) || null;
                // Pick the freshest fetched_at across panes — usually all
                // four are within milliseconds of each other.
                this.snapshotAt = (wa && wa.fetched_at)
                    || (push && push.fetched_at)
                    || (ticks && ticks.fetched_at)
                    || (matches && matches.fetched_at)
                    || null;
                this.error = null;
            } catch (exc) {
                this.error = (exc && exc.message)
                    || (exc && exc.response && exc.response.data && exc.response.data.message)
                    || 'Could not load live monitor state.';
            } finally {
                this.loading = false;
            }
        },

        startPolling () {
            this.refresh();
            this._pollHandle = setInterval(() => this.refresh(), POLL_INTERVAL_MS);
        },

        stopPolling () {
            if (this._pollHandle) {
                clearInterval(this._pollHandle);
                this._pollHandle = null;
            }
        },
    },

    mounted () {
        this.$nextTick(function () {
            this.setBodyClass('admin');
            this.setBreadcrumbItems(this.breadcrumbItems);
            this.animateWrapper();
            this.startPolling();
        });
    },

    beforeDestroy () {
        this.stopPolling();
    },
};
